/**
 * SilkReels 用户端鉴权 hook
 *
 * 状态:token + user,localStorage 持久化,跨页面共享。
 * 会话读写与 refresh 见 `@/api/authSession`。
 */
import { useCallback, useEffect, useState } from 'react'

import {
  ApiError,
  authApi,
  type SilkAuthResult,
} from '@/api'
import {
  fromAuthResult,
  getAuthSnapshot,
  loadAuthSnapshot,
  refreshAuthSession,
  setAuthSnapshot,
  subscribeAuth,
} from '@/api/authSession'

export function useAuth() {
  const [state, setState] = useState(() => getAuthSnapshot() ?? loadAuthSnapshot())

  useEffect(() => {
    const unsub = subscribeAuth(() => {
      setState(getAuthSnapshot())
    })
    return unsub
  }, [])

  const login = useCallback(async (loginId: string, password: string, captchaVerifyParam?: string) => {
    const result = await authApi.login({
      login: loginId,
      password,
      captcha_verify_param: captchaVerifyParam,
    })
    setAuthSnapshot(fromAuthResult(result))
    return result.user
  }, [])

  const loginByPhoneOtp = useCallback(
    async (phone: string, smsCode: string, referralCode?: string, inviteCode?: string) => {
      const result = await authApi.authenticateByPhoneOtp({
        phone,
        sms_code: smsCode,
        referral_code: referralCode || undefined,
        invite_code: inviteCode || undefined,
      })
      setAuthSnapshot(fromAuthResult(result))
      return result
    },
    [],
  )

  const register = useCallback(
    async (input: {
      username?: string
      email?: string
      phone?: string
      sms_code?: string
      email_code?: string
      password: string
      nickname?: string
      referral_code?: string
      tenant_code?: string
      invite_code?: string
      captcha_verify_param?: string
    }) => {
      const result = await authApi.register(input)
      setAuthSnapshot(fromAuthResult(result))
      return result.user
    },
    [],
  )

  const logout = useCallback(async () => {
    const current = getAuthSnapshot()
    setAuthSnapshot(null)
    if (current?.refresh_token) {
      try {
        await authApi.logout(current.refresh_token)
      } catch {
        /* 服务端吊销失败时本地仍按已登出处理 */
      }
    }
  }, [])

  const refresh = useCallback(async (): Promise<SilkAuthResult> => {
    const ok = await refreshAuthSession()
    if (!ok) throw new Error('refresh failed')
    const snap = getAuthSnapshot()
    if (!snap) throw new Error('no session')
    return {
      user: snap.user,
      tokens: {
        access_token: snap.access_token,
        access_expires_at: snap.access_expires_at,
        refresh_token: snap.refresh_token,
        refresh_expires_at: snap.refresh_expires_at,
        token_type: 'Bearer',
      },
    }
  }, [])

  const reloadMe = useCallback(async () => {
    try {
      const user = await authApi.me()
      const snap = getAuthSnapshot()
      if (snap) setAuthSnapshot({ ...snap, user })
      return user
    } catch (e) {
      if (e instanceof ApiError && e.isAuthError()) {
        setAuthSnapshot(null)
      }
      throw e
    }
  }, [])

  return {
    user: state?.user ?? null,
    accessToken: state?.access_token ?? null,
    refreshToken: state?.refresh_token ?? null,
    isAuthenticated: !!state,
    login,
    loginByPhoneOtp,
    register,
    logout,
    refresh,
    reloadMe,
  }
}

/** 在 React 上下文之外读 access token */
export function getAccessToken(): string | null {
  return getAuthSnapshot()?.access_token ?? null
}
